// V9.23 · Cadastro de fornecedores (razão único)
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import api from '@/lib/api';

const EMPTY = { nome: '', documento: '', tipo: 'fornecedor', email: '', telefone: '', observacoes: '', ativo: true };

export const FornecedoresTab = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get('/fin/ledger/suppliers', { params: { q: q || undefined, only_active: false } });
      setItems(data.items || []);
    } catch (_) { toast.error('Erro ao carregar fornecedores.'); }
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  const salvar = async () => {
    if (!form.nome.trim()) return toast.error('Informe o nome.');
    try {
      if (editing) await api.put(`/fin/ledger/suppliers/${editing.id}`, form);
      else await api.post('/fin/ledger/suppliers', form);
      toast.success('Fornecedor salvo.');
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar.'); }
  };

  const excluir = async (f) => {
    try {
      const { data } = await api.delete(`/fin/ledger/suppliers/${f.id}`);
      toast.success(data.message || 'Fornecedor excluído.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao excluir.'); }
  };

  return (
    <Card data-testid="tab-fornecedores">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-lg">Fornecedores e prestadores</CardTitle>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input className="pl-8 w-56" placeholder="Buscar nome ou CNPJ" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="forn-busca" />
          </div>
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}
            data-testid="forn-novo"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nome</TableHead><TableHead>Documento</TableHead><TableHead>Tipo</TableHead>
            <TableHead>Contato</TableHead><TableHead>Status</TableHead><TableHead>Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {!items.length && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                Nenhum fornecedor cadastrado.</TableCell></TableRow>
            )}
            {items.map((f) => (
              <TableRow key={f.id} data-testid={`forn-row-${f.id}`}>
                <TableCell className="font-medium">{f.nome}</TableCell>
                <TableCell>{f.documento || '—'}</TableCell>
                <TableCell className="capitalize">{f.tipo || 'fornecedor'}</TableCell>
                <TableCell className="text-xs">{f.email || f.telefone || '—'}</TableCell>
                <TableCell>
                  <Badge variant={f.ativo === false ? 'secondary' : 'default'}>
                    {f.ativo === false ? 'Inativo' : 'Ativo'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" data-testid={`forn-editar-${f.id}`}
                      onClick={() => { setEditing(f); setForm({ ...EMPTY, ...f }); setOpen(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600"
                      data-testid={`forn-excluir-${f.id}`} onClick={() => excluir(f)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-fornecedor">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar fornecedor' : 'Novo fornecedor'}</DialogTitle>
            <DialogDescription>Usado na busca rápida ao lançar uma despesa.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                data-testid="forn-nome" />
            </div>
            <div>
              <Label>CPF / CNPJ</Label>
              <Input value={form.documento || ''} onChange={(e) => setForm({ ...form, documento: e.target.value })}
                data-testid="forn-doc" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo || 'fornecedor'} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="forn-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fornecedor">Fornecedor</SelectItem>
                  <SelectItem value="prestador">Prestador de serviço</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>E-mail</Label>
              <Input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Telefone</Label>
              <Input value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Observações</Label>
              <Input value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} data-testid="forn-salvar">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FornecedoresTab;
